import { useEffect, useRef } from 'react';
import { useUser } from '../../store/useAuthStore';
import { apiClient } from '../../lib/api';

// Mounts the Zendesk Web Messaging widget for an authenticated user.
// - Loads the Zendesk script tag once per session (keyed on widgetKey).
// - On each user change, signs an identity JWT via the backend and calls
//   zE('messenger', 'loginUser', cb => cb(jwt)) so the visitor is recognized.
// - On logout, calls 'logoutUser' to clear the identity.

declare global {
  interface Window {
    zE?: ((cmd: string, action: string, ...args: any[]) => void) & { [k: string]: any };
    zESettings?: any;
  }
}

export const ZENDESK_SCRIPT_ID = 'ze-snippet';

export const loadZendeskScript = (widgetKey: string): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (document.getElementById(ZENDESK_SCRIPT_ID)) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = ZENDESK_SCRIPT_ID;
    script.async = true;
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(widgetKey)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Zendesk Messaging script'));
    document.head.appendChild(script);
  });
};

// Polls briefly for window.zE to be defined after the script loads, so we
// don't race the widget's bootstrap.
export const waitForZE = async (timeoutMs = 5000): Promise<typeof window.zE | undefined> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (typeof window !== 'undefined' && typeof window.zE === 'function') return window.zE;
    await new Promise(r => setTimeout(r, 100));
  }
  return undefined;
};

export const ZendeskWidget: React.FC = () => {
  const user = useUser();
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Only load the widget for authenticated Bloom users (Phase 1 scope).
      if (!user) {
        const zE = window.zE;
        if (zE && lastUserIdRef.current) {
          try { zE('messenger', 'logoutUser'); } catch {}
        }
        lastUserIdRef.current = null;
        return;
      }

      // Skip if same user already initialized.
      if (lastUserIdRef.current === user._id) return;

      const identity = await apiClient.getZendeskIdentity();
      if (cancelled) return;
      if (!identity.success || !identity.data) {
        // Backend not configured (or transient failure). Silent — we don't
        // want to block the rest of the app's render on a missing widget.
        return;
      }

      const { widgetKey } = identity.data;
      if (!widgetKey) return;

      try {
        await loadZendeskScript(widgetKey);
      } catch {
        return; // Script blocked or offline — give up silently.
      }

      const zE = await waitForZE();
      if (cancelled || !zE) return;

      // Zendesk Messaging will invoke this callback every time it needs a
      // fresh JWT (initial auth, expiry refresh after the 1h TTL, etc).
      // Re-fetching from the backend each time means Zendesk never gets a
      // stale token. Caching the initial JWT in the closure here would
      // break re-auth ~1h into a session.
      zE('messenger', 'loginUser', async (callback: (jwt: string) => void) => {
        try {
          const fresh = await apiClient.getZendeskIdentity();
          if (fresh.success && fresh.data?.token) {
            callback(fresh.data.token);
          } else {
            callback(identity.data!.token); // fallback to the original
          }
        } catch {
          callback(identity.data!.token);
        }
      });

      lastUserIdRef.current = user._id;
    })();

    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  return null;
};

export default ZendeskWidget;
